import type { Dict } from '@aviata/platform-types';

import { appLocale } from '../../shared/model';
import { searchCitiesConfig } from './config';
import {
  LocationType,
  type MappedCity,
  type MappedAirport,
  type MappedCountry,
} from '../../shared/types';

const locale = appLocale.lib.getAppLocale();

type Location =
  | Dict.Cities.City
  | Dict.Cities.City['country']
  | Dict.Airport
  | MappedCity
  | MappedCountry
  | MappedAirport
  | MappedAirport['city'];

const getLocalizedName = (location: Location) => {
  return location[`name_${locale}`];
};

function localizeCountry(country: MappedCountry) {
  return {
    ...country,
    name: getLocalizedName(country),
  };
}

function localizeCity(city: MappedCity | MappedAirport['city']) {
  return {
    ...city,
    country: city.country ? localizeCountry(city.country) : undefined,
    name: getLocalizedName(city),
    children: city.children?.map(child => localizeAirport(child)),
  };
}

function localizeAirport(airport: MappedAirport) {
  return {
    ...airport,
    city: localizeCity(airport.city),
    country: localizeCountry(airport.country),
    name: getLocalizedName(airport),
  };
}

function localizeLocation(
  location: MappedCity | MappedAirport | MappedCountry,
) {
  if (location.type === LocationType.AIRPORT) {
    return localizeAirport(location);
  }
  if (location.type === LocationType.COUNTRY) {
    return localizeCountry(location);
  }
  return localizeCity(location);
}

export const mapLocation = (
  location: Location | null | undefined,
  type: LocationType,
) => {
  if (!location) {
    return null;
  }

  const mappedLocation = {
    name: getLocalizedName(location),
    name_ru: location.name_ru,
    name_kk: location.name_kk,
    name_en: location.name_en,
    code: location.code ?? location.iata ?? location.iso_code,
    type,
  };
  return mappedLocation;
};

const getMappedAirports = (city: Dict.Cities.City): Array<MappedAirport> => {
  return city.airports.map(airport => ({
    ...mapLocation(airport, LocationType.AIRPORT),
    codeForBackend: `${city.iata}${airport.iata}`,
    city: mapLocation(city, LocationType.CITY),
    country: mapLocation(city.country, LocationType.COUNTRY),
  }));
};

const getMappedCities = (
  cities: Array<Dict.Cities.City | MappedCity>,
): Array<MappedCity> => {
  return cities.map(city => ({
    ...mapLocation(city, LocationType.CITY),
    country: mapLocation(city.country, LocationType.COUNTRY),
    children:
      city.airports?.length > 1
        ? getMappedAirports(city as Dict.Cities.City)
        : [],
  }));
};

function getArrivalPopularRoutes() {
  // only needed for localization
  return getMappedCities(searchCitiesConfig.POPULAR_ARRIVALS);
}

function getDeparturePopularRoutes() {
  // only needed for localization
  return getMappedCities(searchCitiesConfig.POPULAR_DEPARTURES);
}

export const citySearchLibs = {
  getArrivalPopularRoutes,
  getDeparturePopularRoutes,
  getMappedCities,
  localizeLocation,
};
